import { createInertiaApp } from '@inertiajs/react'
import { PostHogProvider } from '@posthog/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { initCdn } from '@/lib/cdn'
import { usePostHogIdentify } from '@/hooks/use-posthog-identify'

// eslint-disable-next-line react-refresh/only-export-components -- entrypoint, not a HMR component
function PostHogIdentify({ children }: { children: React.ReactNode }) {
  usePostHogIdentify()
  return children
}

const posthogOptions = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2026-01-30',
} as const

void createInertiaApp({
  progress: false,

  resolve: (name) => {
    const pages = import.meta.glob('../pages/**/*.tsx', {
      eager: true,
    }) as Record<string, { default: React.ComponentType }>
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }
    return page
  },

  setup({ el, App, props }) {
    initCdn((props.initialPage.props as Record<string, unknown>).cdnHost as string | undefined)
    createRoot(el).render(
      <StrictMode>
        <PostHogProvider apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY} options={posthogOptions}>
          <PostHogIdentify>
            <App {...props} />
          </PostHogIdentify>
        </PostHogProvider>
      </StrictMode>
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
